import { useState, useEffect } from 'react';
import { Team, MatchType, Tournament } from '@/types';
import { scheduleMatch, updateScheduledMatch, updateMatchLiveStreamUrl } from '@/lib/supabase';

interface EditMatchData {
  id: string;
  matchNumber: number;
  matchType: MatchType;
  teamAId: string;
  teamBId: string;
  date: string;
  matchTime: string | null;
  liveStreamUrl: string | null;
}

interface ScheduleMatchModalProps {
  tournament: Tournament;
  participatingTeams: Team[];
  onClose: () => void;
  onScheduled: () => void;
  editMatch?: EditMatchData | null;
  isCompleted?: boolean;
}

export default function ScheduleMatchModal({ tournament, participatingTeams, onClose, onScheduled, editMatch, isCompleted = false }: ScheduleMatchModalProps) {
  const [matchNumber, setMatchNumber] = useState<number | ''>(editMatch?.matchNumber ?? '');
  const [matchType, setMatchType] = useState<MatchType>(editMatch?.matchType ?? 'league');
  const [teamAId, setTeamAId] = useState(editMatch?.teamAId ?? '');
  const [teamBId, setTeamBId] = useState(editMatch?.teamBId ?? '');
  const [date, setDate] = useState(editMatch?.date ?? new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState(editMatch?.matchTime ?? '');
  const [liveStreamUrl, setLiveStreamUrl] = useState(editMatch?.liveStreamUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!editMatch;

  // For completed matches only the live stream URL can be changed.
  const readOnly = isCompleted;

  // Re-seed local state if the edit target changes after mount.
  useEffect(() => {
    if (!editMatch) return;
    setMatchNumber(editMatch.matchNumber);
    setMatchType(editMatch.matchType);
    setTeamAId(editMatch.teamAId);
    setTeamBId(editMatch.teamBId);
    setDate(editMatch.date);
    setTime(editMatch.matchTime ?? '');
    setLiveStreamUrl(editMatch.liveStreamUrl ?? '');
  }, [editMatch]);

  const rulesPreview = matchType === 'league'
    ? `${tournament.leagueWinningPoint}/${tournament.leagueGoldenPoint}`
    : `${tournament.playoffWinningPoint}/${tournament.playoffGoldenPoint}`;

  const teamBOptions = participatingTeams.filter(t => t.id !== teamAId);
  const teamAOptions = participatingTeams.filter(t => t.id !== teamBId);

  const handleSubmit = async () => {
    // Completed matches: only update the stream URL
    if (isCompleted && editMatch) {
      setSaving(true);
      setError(null);
      try {
        await updateMatchLiveStreamUrl(editMatch.id, liveStreamUrl || null);
        onScheduled();
      } catch (err: any) {
        setError(err?.message || 'Failed to save. Please try again.');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!matchNumber || matchNumber < 1) { setError('Match number is required'); return; }
    if (!teamAId) { setError('Team A is required'); return; }
    if (!teamBId) { setError('Team B is required'); return; }
    if (teamAId === teamBId) { setError('Teams must be different'); return; }
    if (!date) { setError('Date is required'); return; }

    setSaving(true);
    setError(null);
    try {
      const winningPoint = matchType === 'league' ? tournament.leagueWinningPoint : tournament.playoffWinningPoint;
      const goldenPoint = matchType === 'league' ? tournament.leagueGoldenPoint : tournament.playoffGoldenPoint;

      if (isEditMode && editMatch) {
        await updateScheduledMatch(editMatch.id, {
          matchNumber: Number(matchNumber),
          date,
          matchTime: time || null,
          matchType,
          teamAId,
          teamBId,
          winningPoint,
          goldenPoint,
          liveStreamUrl: liveStreamUrl || null,
        });
      } else {
        await scheduleMatch({
          matchNumber: Number(matchNumber),
          date,
          matchTime: time || null,
          matchType,
          teamAId,
          teamBId,
          tournamentId: tournament.id,
          winningPoint,
          goldenPoint,
          liveStreamUrl: liveStreamUrl || null,
        });
      }
      onScheduled();
    } catch (err: any) {
      setError(err?.message || 'Failed to save match. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-40 flex items-end">
      <div className="w-full max-w-md mx-auto bg-surface border-t border-border rounded-t-2xl p-4 pb-safe animate-slide-up max-h-[90vh] overflow-y-auto">
        <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
        <h2 className="text-lg font-bold text-text-primary text-center mb-1">
          {isCompleted ? 'EDIT STREAM URL' : isEditMode ? 'EDIT MATCH' : 'SCHEDULE MATCH'}
        </h2>
        <p className="text-xs text-text-muted text-center mb-5">{tournament.name}</p>

        <div className="space-y-4 mb-5">
          {/* Match Type */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">Match Type</label>
            <div className="grid grid-cols-2 gap-2">
              {(['league', 'playoff'] as MatchType[]).map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => !readOnly && setMatchType(type)}
                  disabled={readOnly}
                  className={`py-3 rounded-lg font-bold text-sm capitalize border transition-all ${
                    matchType === type
                      ? 'bg-serving/20 border-serving text-serving'
                      : 'bg-surface-raised border-border text-text-secondary hover:border-text-muted'
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  {type.toUpperCase()}
                  <span className="block text-[10px] font-normal mt-0.5 opacity-70">{rulesPreview}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Match Number */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">Match Number</label>
            <input
              type="number"
              inputMode="numeric"
              value={matchNumber}
              onChange={e => setMatchNumber(e.target.value === '' ? '' : Number(e.target.value))}
              className="input-field disabled:opacity-40"
              placeholder="e.g. 1"
              min={1}
              disabled={readOnly}
            />
          </div>

          {/* Teams */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">Team A</label>
            <select
              value={teamAId}
              onChange={e => { setTeamAId(e.target.value); if (e.target.value === teamBId) setTeamBId(''); }}
              className="input-field disabled:opacity-40"
              disabled={readOnly}
            >
              <option value="">Select Team A...</option>
              {teamAOptions.map(t => (
                <option key={t.id} value={t.id}>{t.shortName} — {t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">Team B</label>
            <select
              value={teamBId}
              onChange={e => setTeamBId(e.target.value)}
              className="input-field disabled:opacity-40"
              disabled={!teamAId || readOnly}
            >
              <option value="">Select Team B...</option>
              {teamBOptions.map(t => (
                <option key={t.id} value={t.id}>{t.shortName} — {t.name}</option>
              ))}
            </select>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">Date</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="input-field disabled:opacity-40"
                disabled={readOnly}
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">Time (Optional)</label>
              <input
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                className="input-field disabled:opacity-40"
                disabled={readOnly}
              />
            </div>
          </div>

          {/* Live Streaming URL */}
          <div>
            <label className="block text-xs text-text-muted uppercase tracking-wider mb-1">Live Streaming URL (Optional)</label>
            <input
              type="url"
              value={liveStreamUrl}
              onChange={e => setLiveStreamUrl(e.target.value)}
              className="input-field"
              placeholder="Paste Live Streaming URL (Optional)"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-[10px] text-text-muted mt-1">YouTube, Facebook Live, Vimeo, Zoom, OTT, or any streaming URL.</p>
          </div>

          {/* Preview */}
          {teamAId && teamBId && (
            <div className="bg-surface-raised border border-border rounded-lg p-3">
              <p className="text-xs text-text-muted uppercase tracking-wider mb-2">Preview</p>
              <div className="flex items-center gap-2">
                {(() => {
                  const tA = participatingTeams.find(t => t.id === teamAId);
                  const tB = participatingTeams.find(t => t.id === teamBId);
                  if (!tA || !tB) return null;
                  return (
                    <>
                      <span className="font-bold text-sm" style={{ color: tA.color }}>{tA.shortName}</span>
                      <span className="text-text-muted text-xs">vs</span>
                      <span className="font-bold text-sm" style={{ color: tB.color }}>{tB.shortName}</span>
                      <span className="ml-auto text-xs text-text-muted">{matchType.toUpperCase()} · {rulesPreview}</span>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {error && <p className="text-error text-sm mb-3">{error}</p>}

        <div className="space-y-2">
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn-primary bg-serving text-white font-bold disabled:opacity-50"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {isEditMode ? 'SAVING...' : 'SCHEDULING...'}
              </span>
            ) : isEditMode ? 'SAVE CHANGES' : 'SCHEDULE MATCH'}
          </button>
          <button onClick={onClose} disabled={saving} className="btn-secondary text-text-secondary">
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}
